import { useState, useEffect } from 'react';

export const useKeyboardNavigation = ({ rows, fields, onNavigationStart, onKeyDown }) => {
  const [activeCell, setActiveCell] = useState(null);

  // Update active cell whenever focus changes
  useEffect(() => {
    const handleFocusChange = () => {
      const activeElement = document.activeElement;
      if (!activeElement) {
        setActiveCell(null);
        return;
      }

      const rowIndex = activeElement.getAttribute('data-row');
      const fieldKey = activeElement.getAttribute('data-field');
      
      if (rowIndex !== null && fieldKey !== null) {
        const fieldIndex = fields.findIndex(f => f.key === fieldKey);
        setActiveCell({ rowIndex: parseInt(rowIndex), fieldIndex });
      } else {
        setActiveCell(null);
      }
    };

    // Initial check
    handleFocusChange();

    // Listen for focus changes
    document.addEventListener('focusin', handleFocusChange);
    document.addEventListener('focusout', handleFocusChange);

    return () => {
      document.removeEventListener('focusin', handleFocusChange);
      document.removeEventListener('focusout', handleFocusChange);
    };
  }, [fields]);

  const getFocusableElement = (rowIndex, fieldIndex) => {
    const input = document.querySelector(
      `[data-row="${rowIndex}"][data-field="${fields[fieldIndex].key}"]`
    );
    return input;
  };

  const handleArrowKey = (e, currentRowIndex, currentFieldIndex) => {
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
    
    e.preventDefault();
    onNavigationStart?.();
    
    let newRowIndex = currentRowIndex;
    let newFieldIndex = currentFieldIndex;

    switch (e.key) {
      case 'ArrowUp':
        newRowIndex = Math.max(0, currentRowIndex - 1);
        break;
      case 'ArrowDown':
        if (currentRowIndex === rows.length - 1) {
          // Let the parent handle adding a new row
          onKeyDown?.(e, currentRowIndex, currentFieldIndex, fields);
          return;
        } else {
          newRowIndex = Math.min(rows.length - 1, currentRowIndex + 1);
        }
        break;
      case 'ArrowLeft':
        newFieldIndex = Math.max(0, currentFieldIndex - 1);
        break;
      case 'ArrowRight':
        newFieldIndex = Math.min(fields.length - 1, currentFieldIndex + 1);
        break;
      default:
        break;
    }

    // Skip the remove row button column for left/right navigation
    if (newFieldIndex === -1) newFieldIndex = 0;

    const nextInput = getFocusableElement(newRowIndex, newFieldIndex);
    if (nextInput) {
      nextInput.focus();
      if (nextInput.type === 'text') {
        const len = nextInput.value.length;
        nextInput.setSelectionRange(len, len);
      }
    }
  };

  const handleKeyDown = (e, rowIndex, fieldIndex, fields) => {
    handleArrowKey(e, rowIndex, fieldIndex);

    if (['Tab', 'Enter'].includes(e.key)) {
      onNavigationStart?.();
      const nextFieldIndex = e.shiftKey ? fieldIndex - 1 : fieldIndex + 1;
      let nextRowIndex = rowIndex;

      if (nextFieldIndex >= fields.length) {
        nextRowIndex = rowIndex + 1;
        if (nextRowIndex < rows.length) {
          const nextInput = getFocusableElement(nextRowIndex, 0);
          if (nextInput) {
            e.preventDefault();
            nextInput.focus();
          }
        }
      } else if (nextFieldIndex < 0) {
        nextRowIndex = rowIndex - 1;
        if (nextRowIndex >= 0) {
          const nextInput = getFocusableElement(nextRowIndex, fields.length - 1);
          if (nextInput) {
            e.preventDefault();
            nextInput.focus();
          }
        }
      }
    }
  };

  return {
    handleKeyDown,
    activeCell
  };
};
